/**
 * API service for the Assignments page (Tab 2 of the Station Assignment workflow).
 *
 * Mirrors the `getShiftRoster` pattern: reads `stationCode` from the Redux
 * store, builds a typed request, calls `apiRequest` (which is routed through
 * `apiMiddleware` to `MockDataService.getAssignments` in local environments),
 * and dispatches `showError` on any failure while returning `EMPTY_RESPONSE`.
 */
import { store } from '../../../store';
import { selectStationCode } from '../../../store/slices/shiftPlanSlice';
import { apiRequest } from '../../../services/apiMiddleware';
import { API_GATEWAY_PROCESS_NAME } from '../../../utils/constants';
import { showError } from '../../../store/slices/errorSlice';
import { EMPTY_RESPONSE, GetAssignmentsRequest, GetAssignmentsResponse } from './models';

/**
 * Fetch assignments data for the given date, shift, and plan.
 *
 * @param ofdDate The selected date (OFD date)
 * @param clockWindowName The shift window (e.g. DAY or NIGHT)
 * @param planId The station assignment plan ID
 * @returns Promise with the GetAssignmentsResponse; returns EMPTY_RESPONSE on any failure
 */
export const getAssignments = async (
  ofdDate: string,
  clockWindowName: string,
  planId: string,
): Promise<GetAssignmentsResponse> => {
  try {
    const state = store.getState();
    const stationCode = selectStationCode(state);

    if (!stationCode) {
      store.dispatch(showError({
        title: 'Warehouse ID Required',
        message: 'Please select a valid warehouse ID from the dropdown',
      }));
      return EMPTY_RESPONSE;
    }

    const requestBody: GetAssignmentsRequest = {
      nodeId: stationCode,
      ofdDate,
      clockWindowName,
      planId,
    };

    const requestPayload = {
      resourcePath: '/getAssignments',
      httpMethod: 'post',
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    };

    const response = await apiRequest(requestPayload);

    if (response.status && response.status >= 400) {
      store.dispatch(showError({
        title: 'Failed to fetch assignments data',
        message: response.errorResponse || 'Unable to fetch assignments data. Please try again later.',
      }));
      return EMPTY_RESPONSE;
    }

    return response.data as GetAssignmentsResponse;
  } catch (error: any) {
    console.error('Error fetching assignments data:', error);
    store.dispatch(showError({
      title: 'Failed to fetch assignments data',
      message: error.errorResponse || error.message || 'Unable to fetch assignments data. Please try again later.',
    }));
    return EMPTY_RESPONSE;
  }
};
